import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog } from "primereact/dialog";
import { TabPanel, TabView } from "primereact/tabview";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAnalytics } from "../../lib/analytics";
import * as documentService from "../../services/documentService";
import type {
	Document,
	DocumentCategory,
	DocumentShare,
	Provider,
} from "../../types/document";
import { Dropdown } from "../ui/form";
import styles from "./DocumentDetailModal.module.css";

interface DocumentDetailModalProps {
	document: Document;
	isOpen: boolean;
	onClose: () => void;
	onDelete: (documentId: string) => void;
	onCategoryChange: (documentId: string, category: DocumentCategory) => void;
}

export function DocumentDetailModal({
	document,
	isOpen,
	onClose,
	onDelete,
	onCategoryChange,
}: DocumentDetailModalProps) {
	const { t } = useTranslation("documents");
	const { logEvent } = useAnalytics();
	const queryClient = useQueryClient();
	const [activeTab, setActiveTab] = useState(0);
	const [shares, setShares] = useState<DocumentShare[]>([]);
	const [providers, setProviders] = useState<Provider[]>([]);
	const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	// Fetch document analysis with React Query for caching
	const { data: analysis, isLoading: isLoadingAnalysis } = useQuery({
		queryKey: ["document-analysis", document.id],
		queryFn: async () => {
			const response = await documentService.getDocumentAnalysis(document.id);
			return response.analyses[0] || null;
		},
		enabled: activeTab === 2,
		staleTime: 1000 * 60 * 5, // 5 minutes
	});

	// Mutation for triggering document analysis
	const analyzeDocumentMutation = useMutation({
		mutationFn: () => documentService.analyzeDocument(document.id),
		onSuccess: (response) => {
			queryClient.setQueryData(
				["document-analysis", document.id],
				response.analysis,
			);
			toast.success(t("analysis.complete"));
			logEvent("document_analysis_completed", undefined, {
				documentId: document.id,
			});
		},
		onError: () => {
			toast.error(t("errors.analysisFailed"));
			logEvent("document_analysis_failed", undefined, {
				documentId: document.id,
			});
		},
	});

	// Load shares when switching to tabs
	const handleTabChange = async (index: number) => {
		setActiveTab(index);

		if (index === 1) {
			logEvent("document_sharing_tab_clicked", undefined, {
				documentId: document.id,
			});
			if (shares.length === 0) {
				await fetchShares();
				await fetchProviders();
			}
		} else if (index === 2) {
			logEvent("document_analysis_tab_clicked", undefined, {
				documentId: document.id,
				hasExistingAnalysis: !!analysis,
			});
			// useQuery automatically fetches when enabled
		}
	};

	const fetchShares = async () => {
		try {
			const response = await documentService.getDocumentShares(document.id);
			setShares(response.shares);
		} catch (error) {
			console.error("Error fetching shares:", error);
			toast.error(t("errors.fetchSharesFailed"));
		}
	};

	const fetchProviders = async () => {
		try {
			// TODO: Replace with actual API call
			// const response = await userService.getProviders();
			// setProviders(response.providers);

			setProviders([]);
		} catch (error) {
			console.error("Error fetching providers:", error);
		}
	};

	const handleShare = async () => {
		logEvent("document_share_button_clicked", undefined, {
			documentId: document.id,
			selectedProviderCount: selectedProviders.length,
		});

		if (selectedProviders.length === 0) {
			toast.error(t("share.selectProvider"));
			return;
		}

		try {
			await documentService.shareDocument(document.id, selectedProviders);

			await fetchShares();
			setSelectedProviders([]);
			toast.success(t("share.success"));

			logEvent("document_shared", undefined, {
				documentId: document.id,
				providerCount: selectedProviders.length,
			});
		} catch (error) {
			console.error("Error sharing document:", error);
			toast.error(t("errors.shareFailed"));
		}
	};

	const handleProviderToggle = (provider: Provider, checked: boolean) => {
		logEvent("document_share_provider_toggled", undefined, {
			documentId: document.id,
			providerId: provider.id,
			providerName: provider.name,
			checked,
		});

		if (checked) {
			setSelectedProviders((prev) => [...prev, provider.id]);
		} else {
			setSelectedProviders((prev) => prev.filter((id) => id !== provider.id));
		}
	};

	const handleRevokeShare = async (shareId: string, providerId: string) => {
		try {
			await documentService.revokeDocumentShare(document.id, providerId);

			setShares((prev) => prev.filter((share) => share.id !== shareId));
			toast.success(t("share.revoked"));

			logEvent("document_share_revoked", undefined, {
				documentId: document.id,
				providerId,
			});
		} catch (error) {
			console.error("Error revoking share:", error);
			toast.error(t("errors.revokeFailed"));
		}
	};

	const handleAnalyze = () => {
		logEvent("document_analyze_clicked", undefined, {
			documentId: document.id,
			mimeType: document.mimeType,
		});
		analyzeDocumentMutation.mutate();
	};

	const handleRefreshAnalysis = () => {
		logEvent("document_analysis_refresh_clicked", undefined, {
			documentId: document.id,
		});
		analyzeDocumentMutation.mutate();
	};

	const handleDownload = async () => {
		logEvent("document_download_clicked", undefined, {
			documentId: document.id,
			mimeType: document.mimeType,
		});

		try {
			// Fetch the file and trigger download
			const response = await fetch(document.fileUrl);
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const link = window.document.createElement("a");
			link.href = url;
			link.download = document.name;
			window.document.body.appendChild(link);
			link.click();
			window.document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Error downloading file:", error);
			// Fallback to opening in new tab
			window.open(document.fileUrl, "_blank");
		}
	};

	const handleDeleteClick = () => {
		logEvent("document_delete_clicked", undefined, {
			documentId: document.id,
		});
		setShowDeleteConfirm(true);
	};

	const handleDeleteConfirm = () => {
		logEvent("document_delete_confirmed", undefined, {
			documentId: document.id,
		});
		onDelete(document.id);
		setShowDeleteConfirm(false);
	};

	const handleDeleteCancel = () => {
		logEvent("document_delete_cancelled", undefined, {
			documentId: document.id,
		});
		setShowDeleteConfirm(false);
	};

	const handleClose = () => {
		logEvent("document_modal_closed", undefined, {
			documentId: document.id,
			activeTab,
		});
		onClose();
	};

	const formatDate = (dateString: string): string => {
		const date = new Date(dateString);
		return date.toLocaleDateString(undefined, {
			year: "numeric",
			month: "long",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	const categories: Array<{ value: DocumentCategory; label: string }> = [
		{ value: "lab_results", label: t("categories.lab_results") },
		{ value: "prescriptions", label: t("categories.prescriptions") },
		{ value: "imaging", label: t("categories.imaging") },
		{
			value: "medical_records",
			label: t("categories.medical_records"),
		},
		{ value: "insurance", label: t("categories.insurance") },
		{ value: "other", label: t("categories.other") },
	];

	const renderPreview = () => {
		if (document.mimeType.startsWith("image/")) {
			return (
				<img
					src={document.fileUrl}
					alt={document.name}
					className={styles.imagePreview}
				/>
			);
		}

		if (document.mimeType === "application/pdf") {
			return (
				<iframe
					src={document.fileUrl}
					title={document.name}
					className={styles.pdfPreview}
				/>
			);
		}

		return (
			<div className={styles.noPreview}>
				<i className="pi pi-file" style={{ fontSize: "4rem" }} />
				<p>{t("detail.noPreview")}</p>
			</div>
		);
	};

	const headerTemplate = (
		<div className={styles.modalHeader}>
			<span className={styles.modalTitle}>{document.name}</span>
			<button
				type="button"
				onClick={handleClose}
				className={styles.closeButton}
				aria-label={t("detail.close")}
			>
				<i className="pi pi-times" />
			</button>
		</div>
	);

	return (
		<Dialog
			visible={isOpen}
			onHide={handleClose}
			header={headerTemplate}
			className={styles.modal}
			style={{ width: "90vw", maxWidth: "1000px" }}
			dismissableMask
		>
			<TabView
				activeIndex={activeTab}
				onTabChange={(e) => handleTabChange(e.index)}
			>
				{/* Details Tab */}
				<TabPanel header={t("tabs.details")}>
					<div className={styles.detailsTab}>
						<div className={styles.preview}>{renderPreview()}</div>

						<div className={styles.metadata}>
							<div className={styles.metadataGroup}>
								<span className={styles.metadataLabel}>
									{t("detail.category")}
								</span>
								<Dropdown
									options={categories}
									value={document.category}
									onChange={(value) => {
										logEvent("document_category_changed", undefined, {
											documentId: document.id,
											oldCategory: document.category,
											newCategory: value,
										});
										onCategoryChange(document.id, value as DocumentCategory);
									}}
									placeholder={t("categories.other")}
									className={styles.categoryDropdown}
								/>
							</div>

							{document.description && (
								<div className={styles.metadataGroup}>
									<span className={styles.metadataLabel}>
										{t("detail.description")}
									</span>
									<p className={styles.metadataValue}>{document.description}</p>
								</div>
							)}

							<div className={styles.metadataGroup}>
								<span className={styles.metadataLabel}>
									{t("detail.fileSize")}
								</span>
								<p className={styles.metadataValue}>
									{formatFileSize(document.fileSize)}
								</p>
							</div>

							<div className={styles.metadataGroup}>
								<span className={styles.metadataLabel}>
									{t("detail.fileType")}
								</span>
								<p className={styles.metadataValue}>{document.mimeType}</p>
							</div>

							<div className={styles.metadataGroup}>
								<span className={styles.metadataLabel}>
									{t("detail.uploadedAt")}
								</span>
								<p className={styles.metadataValue}>
									{formatDate(document.uploadedAt)}
								</p>
							</div>

							<div className={styles.actions}>
								<button
									type="button"
									onClick={handleDownload}
									className={styles.downloadButton}
								>
									<i className="pi pi-download" />
									{t("detail.download")}
								</button>

								<button
									type="button"
									onClick={handleDeleteClick}
									className={styles.deleteButton}
								>
									<i className="pi pi-trash" />
									{t("detail.delete")}
								</button>
							</div>
						</div>
					</div>
				</TabPanel>

				{/* Sharing Tab */}
				<TabPanel header={t("tabs.sharing")}>
					<div className={styles.sharingTab}>
						<div className={styles.shareForm}>
							<h3>{t("share.title")}</h3>
							<p className={styles.shareDescription}>
								{t("share.description")}
							</p>

							<div className={styles.providerSelect}>
								{providers.map((provider) => (
									<label key={provider.id} className={styles.providerOption}>
										<input
											type="checkbox"
											checked={selectedProviders.includes(provider.id)}
											onChange={(e) =>
												handleProviderToggle(provider, e.target.checked)
											}
										/>
										<div className={styles.providerInfo}>
											<span className={styles.providerName}>
												{provider.name}
											</span>
											{provider.specialty && (
												<span className={styles.providerSpecialty}>
													{provider.specialty}
												</span>
											)}
										</div>
									</label>
								))}
							</div>

							<button
								type="button"
								onClick={handleShare}
								disabled={selectedProviders.length === 0}
								className={styles.shareButton}
							>
								<i className="pi pi-share-alt" />
								{t("share.shareButton")}
							</button>
						</div>

						{shares.length > 0 && (
							<div className={styles.sharedList}>
								<h3>{t("share.sharedWith")}</h3>
								{shares.map((share) => (
									<div key={share.id} className={styles.sharedItem}>
										<div className={styles.sharedProvider}>
											<i className="pi pi-user" />
											<div>
												<p className={styles.sharedProviderName}>
													{share.provider.name}
												</p>
												<p className={styles.sharedDate}>
													{t("share.sharedOn", {
														date: formatDate(share.createdAt),
													})}
												</p>
											</div>
										</div>
										<button
											type="button"
											onClick={() =>
												handleRevokeShare(share.id, share.providerId)
											}
											className={styles.revokeButton}
										>
											{t("share.revoke")}
										</button>
									</div>
								))}
							</div>
						)}
					</div>
				</TabPanel>

				{/* Analysis Tab */}
				<TabPanel header={t("tabs.analysis")}>
					<div className={styles.analysisTab}>
						{!analysis &&
							!isLoadingAnalysis &&
							!analyzeDocumentMutation.isPending && (
								<div className={styles.analysisPrompt}>
									<i className="pi pi-sparkles" style={{ fontSize: "3rem" }} />
									<h3>{t("analysis.title")}</h3>
									<p>{t("analysis.description")}</p>
									<button
										type="button"
										onClick={handleAnalyze}
										className={styles.analyzeButton}
									>
										<i className="pi pi-bolt" />
										{t("analysis.analyzeButton")}
									</button>
								</div>
							)}

						{(isLoadingAnalysis || analyzeDocumentMutation.isPending) &&
							!analysis && (
								<div className={styles.analyzing}>
									<i
										className="pi pi-spin pi-spinner"
										style={{ fontSize: "3rem" }}
									/>
									<p>{t("analysis.analyzing")}</p>
								</div>
							)}

						{analysis && (
							<div className={styles.analysisResults}>
								<div className={styles.analysisHeader}>
									<span className={styles.analysisTimestamp}>
										{t("analysis.analyzedOn", {
											date: formatDate(analysis.createdAt),
										})}
									</span>
									<button
										type="button"
										onClick={handleRefreshAnalysis}
										disabled={analyzeDocumentMutation.isPending}
										className={styles.refreshButton}
									>
										<i
											className={
												analyzeDocumentMutation.isPending
													? "pi pi-spin pi-spinner"
													: "pi pi-refresh"
											}
										/>
										{t("analysis.refresh")}
									</button>
								</div>

								{analysis.results.summary && (
									<div className={styles.analysisSection}>
										<h3>{t("analysis.summary")}</h3>
										<p>{analysis.results.summary}</p>
									</div>
								)}

								{analysis.results.keyInformation &&
									analysis.results.keyInformation.length > 0 && (
										<div className={styles.analysisSection}>
											<h3>{t("analysis.keyInformation")}</h3>
											<div className={styles.keyInfo}>
												{analysis.results.keyInformation.map((info, index) => (
													<div key={index} className={styles.keyInfoItem}>
														<span className={styles.keyInfoCategory}>
															{info.category}
														</span>
														<span className={styles.keyInfoValue}>
															{info.value}
														</span>
													</div>
												))}
											</div>
										</div>
									)}

								{analysis.results.actionItems &&
									analysis.results.actionItems.length > 0 && (
										<div className={styles.analysisSection}>
											<h3>{t("analysis.actionItems")}</h3>
											<ul className={styles.actionItems}>
												{analysis.results.actionItems.map((item, index) => (
													<li key={index}>{item}</li>
												))}
											</ul>
										</div>
									)}
							</div>
						)}
					</div>
				</TabPanel>
			</TabView>

			{/* Delete Confirmation Dialog */}
			{showDeleteConfirm && (
				<Dialog
					visible={showDeleteConfirm}
					onHide={handleDeleteCancel}
					header={t("delete.confirmTitle")}
					className={styles.deleteModal}
					footer={
						<div className={styles.confirmActions}>
							<button
								type="button"
								onClick={handleDeleteCancel}
								className={styles.cancelButton}
							>
								{t("delete.cancel")}
							</button>
							<button
								type="button"
								onClick={handleDeleteConfirm}
								className={styles.confirmDeleteButton}
							>
								{t("delete.confirm")}
							</button>
						</div>
					}
				>
					<p>{t("delete.confirmMessage")}</p>
				</Dialog>
			)}
		</Dialog>
	);
}
